import PropTypes from 'prop-types';
import React, { Component } from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import Button from 'yoda-core-components/lib/components/Button';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import MessageBox from 'yoda-core-components/lib/components/MessageBox/MessageBox';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import get from 'lodash/get';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import InputValidate from 'yoda-account-components/lib/components/InputValidate/InputValidate';
import PhoneInput from 'yoda-account-components/lib/components/PhoneInput/PhoneInput';
import { trackOrder, enableInputFieldMaskFix } from 'yoda-account-components/lib/common/Constants';

const cx = classNames.bind();

class TrackOrders extends Component {
    static propTypes = {
        submitHandler: PropTypes.func.isRequired,
        featureFlags: PropTypes.objectOf(PropTypes.object),
        preferences: PropTypes.objectOf(PropTypes.object),
        messagesTexts: PropTypes.objectOf(PropTypes.object),
        trackOrder: PropTypes.objectOf(PropTypes.string).isRequired,
        deviceType: PropTypes.shape({
            isDesktop: PropTypes.bool,
            isMobile: PropTypes.bool,
            isTablet: PropTypes.bool,
        }).isRequired,
        accountAction: PropTypes.objectOf(PropTypes.func).isRequired,
        actions: PropTypes.objectOf(PropTypes.func).isRequired,
        authenticationSuccess: PropTypes.bool.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
            search: PropTypes.objectOf.isRequired,
        }).isRequired,
        createAccountSuccess: PropTypes.bool.isRequired,
        triggerFormError: PropTypes.func,
    };

    static defaultProps = {
        triggerFormError: noop,
        featureFlags: {},
        messagesTexts: {},
        preferences: {},
    };

    constructor(props) {
        /* istanbul ignore next */
        super(props);
        let userState;
        /* istanbul ignore else */
        if (!__SERVER__) {
            userState = TokenProvider.get('DP_USER_STATE');
        }

        const isLoggedIn = userState === '1' || userState === '2';

        const query = makeQuery(get(props, 'location.search', {}));
        let orderNumberValue = query.orderNumber || '';
        const orderConfig = get(trackOrder, 'inputFields.orderNumber', {});
        if (orderNumberValue) {
            orderNumberValue = orderNumberValue.replace(orderConfig.onlyNum, '');
        }
        this.validateOrder(orderNumberValue, userState, isLoggedIn);
        this.state = {
            showMessage: false,
            isValid: true,
            isLoggedIn,
            orderNumber: orderNumberValue,
            phoneNumber: '',
            emptyInputError: false,
            emptyPhoneInputError: false,
            messageTitle: '',
            messageType: 'error',
            orderNumberValue,
        };
        this.onOrderNumberChange = this.onOrderNumberChange.bind(this);
        this.onPhoneNumberChange = this.onPhoneNumberChange.bind(this);
        this.validateSubmit = this.validateSubmit.bind(this);
        this.gotoMyOrders = this.gotoMyOrders.bind(this);
        this.gotoSignIn = this.gotoSignIn.bind(this);
        this.closeMessageBox = this.closeMessageBox.bind(this);
    }

    componentDidMount() {
        const { actions: { trackOrderOnLoad = noop } = {} } = this.props;
        trackOrderOnLoad('track order');
    }

    /* eslint-disable camelcase */
    UNSAFE_componentWillReceiveProps(nextProps) {
        const {
            trackOrder,
            location: { pathname },
            preferences,
            authenticationSuccess,
            createAccountSuccess,
        } = nextProps;
        if (
            !createAccountSuccess &&
            authenticationSuccess &&
            preferences.authenticationSuccessRedirects[pathname]
        ) {
            window.location.assign(preferences.authenticationSuccessRedirects[pathname]);
        } else if (get(trackOrder, 'isLoading')) {
            this.closeMessageBox();
        } else if (
            get(trackOrder, 'isSuccess') &&
            get(trackOrder, 'isSuccess') !== get(this.props.trackOrder, 'isSuccess')
        ) {
            // should be fresh response from fresh request
            // then only redirect to order details
            this.gotoOrderDetails();
        } else if (
            !isEqual(get(this.props.trackOrder, 'error'), get(trackOrder, 'error')) &&
            get(trackOrder, 'error')
        ) {
            let messageTitle = get(
                this.props.messagesTexts,
                'trackOrder.trackOrderDownMessage',
                ''
            );
            let messageType = 'error';
            /* istanbul ignore else */
            if (!isEmpty(trackOrder.error)) {
                messageTitle = this.props.preferences.trackOrder.orderNotFoundText;
                messageType = 'error';
            }
            this.setState({
                messageType,
                messageTitle,
                showMessage: true,
            });
            this.trackFormError([messageTitle]);
        }
    }

    onOrderNumberChange(isValid, value) {
        if (isValid) {
            this.setState({ orderNumber: value, isValid: true });
        } else if (!value) {
            this.setState({
                emptyInputError: true,
                isValid: false,
            });
        } else {
            this.setState({
                isValid: false,
            });
        }
    }

    onPhoneNumberChange(isValid, value) {
        if (isValid) {
            this.setState({ phoneNumber: value, isValid: true });
        } else if (!value) {
            this.setState({
                emptyPhoneInputError: true,
                isValid: false,
            });
        } else {
            this.setState({
                isValid: false,
            });
        }
    }

    trackFormError = (errorMessages = []) => {
        const { triggerFormError } = this.props;
        let errorDetails = [];
        if (Array.isArray(errorMessages)) {
            errorDetails = errorMessages.map((errorDescription) => ({ errorDescription }));
        }
        triggerFormError(errorDetails);
    };

    gotoOrderDetails() {
        const { preferences } = this.props;
        let { orderNumber } = this.state;
        const orderConfig = get(trackOrder, 'inputFields.orderNumber', {});
        if (orderNumber) {
            orderNumber = orderNumber.replace(orderConfig.onlyNum, '');
        }
        const url = `${preferences.navigateToOrderDetails}?id=${orderNumber}`;
        // Logic removed due to PII issue (never pass PII data in query param)
        // if (!isLoggedIn) {
        //     // for guest user we need to append phone number as well.
        //     url += `&phone=${phoneNumber}`;
        // }
        getBrowserHistory().push(url);
    }

    validateOrder(orderNumberValue, userState, isLoggedIn) {
        const { preferences, location } = this.props;
        const query = makeQuery(get(location, 'search', {}));
        const cid = query.cid ? `cid=${query.cid}` : '';
        if (orderNumberValue && userState === '1') {
            getBrowserHistory().push(
                `${preferences.navigateToOrderDetails}?id=${orderNumberValue}&${cid}`
            );
        } else if (orderNumberValue && userState === '2') {
            getBrowserHistory().push(`${preferences.navigateToOrders}?${cid}`);
        }
        /**
         * This is the case when orderNumber does not exist in queryString
         */
        if (isLoggedIn && !orderNumberValue) {
            getBrowserHistory().push(`${preferences.navigateToOrders}?${cid}`);
        }
    }

    validateSubmit(ev) {
        ev.preventDefault();
        const { phoneNumber, isValid } = this.state;
        let { orderNumber } = this.state;
        const orderConfig = get(trackOrder, 'inputFields.orderNumber', {});
        const phoneNumberConfig = get(trackOrder, 'inputFields.phoneNumber', {});
        if (isValid) {
            const errorMessages = [];
            if (!orderNumber) {
                this.setState({
                    emptyInputError: true,
                    isValid: false,
                });
                errorMessages.push(orderConfig.defaultError);
            }
            if (!phoneNumber) {
                this.setState({
                    emptyPhoneInputError: true,
                    isValid: false,
                });
                errorMessages.push(phoneNumberConfig.defaultError);
            }
            this.trackFormError(errorMessages);
            /**
             * Only if Ordernumber and Phonenumber is present
             */
            orderNumber = orderNumber.replace(orderConfig.onlyNum, '');
            if (orderNumber && phoneNumber) {
                this.setState({ showMessage: false, messageTitle: '' });
                this.props.submitHandler({
                    orderNumber,
                    params: { phone: phoneNumber },
                    skipReduxCache: true,
                });
            }
        }
    }

    closeMessageBox() {
        this.setState({ showMessage: false, messageTitle: '' });
    }

    gotoMyOrders() {
        const { preferences } = this.props;
        getBrowserHistory().push(preferences.navigateToOrders);
    }

    gotoSignIn() {
        const { preferences, deviceType, featureFlags, accountAction } = this.props;
        if (featureFlags.enableSignInSlider) {
            accountAction.openSignInSlider(false, undefined);
        } else {
            window.location.assign(`${preferences.navigateToSignIn[deviceType]}?next=/orders`);
        }
    }

    render() {
        const { featureFlags, messagesTexts } = this.props;
        const { emptyInputError, emptyPhoneInputError, isLoggedIn, orderNumberValue } = this.state;

        const inputFieldConfig = get(trackOrder, 'inputFields', {});
        const { orderNumber, phoneNumber } = inputFieldConfig;
        const { isTrackOrderORPart = true } = featureFlags || {};
        return (
            <div
                className={`${cx('trackOrderPage')} ${dt([
                    'bg-gray-15',
                    'w-full',
                    'block',
                    'mt-6',
                    'smOnly:p-0',
                    'mb-10',
                ])}`}
            >
                <div
                    className={`${cx('trackOrder')} ${dt([
                        'h-full',
                        'bg-white',
                        'my-0',
                        'mx-auto',
                        'w-full',
                        'tailwindGrid',
                        'text-center',
                    ])}`}
                >
                    <div
                        className={`${cx('trackOrderContent')} ${dt([
                            'text-center',
                            'bg-white',
                            'justify-center',
                            'sm12',
                            'md6',
                            'lg4',
                            'xl4',
                            'inline-block',
                            '!px-0',
                        ])}`}
                    >
                        <form
                            ref={
                                /* istanbul ignore next */ (component) => {
                                    this.form = component;
                                }
                            }
                            noValidate
                            onSubmit={this.validateSubmit}
                            className={dt(['smOnly:mx-6'])}
                        >
                            <h2
                                className={dt([
                                    'text-xLg',
                                    'font-semibold',
                                    'leading-8',
                                    'text-center',
                                    'text-black',
                                    'mb-6',
                                ])}
                            >
                                Order Status
                            </h2>
                            <MessageBox
                                level="section"
                                showMessage={this.state.showMessage}
                                title={this.state.messageTitle}
                                onClose={this.closeMessageBox}
                                className={`${'TrackOrders-messageBoxLayout'} ${dt([
                                    'text-left',
                                    'md:my-0',
                                    'smOnly:my-0',
                                    '!mb-6',
                                ])}`}
                                type={this.state.messageType}
                            >
                                <div>{this.state.messageContent}</div>
                            </MessageBox>
                            <div
                                className={`${cx('formInput')} ${dt([
                                    'float-left',
                                    'w-full',
                                    'mb-6',
                                    'text-left',
                                    'text-gray-70',
                                ])}`}
                            >
                                <InputValidate
                                    isInputValidated={(valid, value) =>
                                        this.onOrderNumberChange(valid, value)
                                    }
                                    emptyInputError={emptyInputError}
                                    defaultError={orderNumber.defaultError}
                                    label="Order Number"
                                    isOrderNumber
                                    name={orderNumber.name}
                                    type={orderNumber.type}
                                    defaultValue={orderNumberValue}
                                    datatlPrivate
                                    dataQmBlock
                                    inputClassName={dt(['!shadow-none', '!py-3'])}
                                    convertLabelToAnimatingLegend
                                />
                            </div>
                            <div
                                className={`${cx('formInput')} ${dt([
                                    'float-left',
                                    'w-full',
                                    'mb-6',
                                    'text-left',
                                    'text-gray-70',
                                ])}`}
                            >
                                <PhoneInput
                                    phoneInputId="track_order_phone"
                                    labelValue={phoneNumber.label}
                                    name={phoneNumber.name}
                                    isPhoneValidated={this.onPhoneNumberChange}
                                    emptyInputError={emptyPhoneInputError}
                                    placeHolder=""
                                    enableInputFieldMaskFix={enableInputFieldMaskFix}
                                    inputClassName={dt(['!rounded-lg', '!py-3', '!px-5'])}
                                    labelClassName={dt(['!leading-6'])}
                                    convertLabelToAnimatingLegend
                                    newIconForPersonalInfoAndPreferences
                                />
                            </div>
                            <div
                                className={`${cx('trackOrderBtn')} ${dt([
                                    'w-full',
                                    'mx-0',
                                    'inline-block',
                                    'items-center',
                                    'flex',
                                    'justify-center',
                                ])}`}
                            >
                                <Button
                                    type="submit"
                                    buttonType="Primary"
                                    size="Lg"
                                    className={`4{cx('submitTrackOrder')} ${dt([
                                        'lg:w-9/12',
                                        'smMd:w-full',
                                        'text-center',
                                    ])}`}
                                >
                                    Find Order
                                </Button>
                            </div>
                        </form>
                        {isTrackOrderORPart && (
                            <div className={dt(['smOnly:mx-6'])}>
                                <div
                                    className={`${cx('or')} ${dt([
                                        'w-full',
                                        'border-solid',
                                        'border-b-[1px]',
                                        'border-gray-15',
                                        'my-0',
                                        'mx-auto',
                                        'text-center',
                                        'relative',
                                        'mt-5',
                                        'mb-5',
                                    ])}`}
                                >
                                    <span
                                        className={dt([
                                            'text-base',
                                            'font-semibold',
                                            'text-black',
                                            'text-center',
                                            'absolute',
                                            '-top-[0.85rem]',
                                            'left-[44%]',
                                            'bg-white',
                                            'py-0',
                                            'px-2.5',
                                        ])}
                                    >
                                        or
                                    </span>
                                </div>
                                {isLoggedIn ? (
                                    <div>
                                        <p
                                            className={`${cx('subText')} ${dt([
                                                'mb-4',
                                                'float-left',
                                                'w-full',
                                                'text-medium',
                                                'leading-[1.5]',
                                                'text-center',
                                                'text-gray-70',
                                                'smOnly:text-left',
                                            ])}`}
                                        >
                                            {messagesTexts.trackOrder.viewMyOrdersText}
                                        </p>
                                        <div className={`${cx('viewOrderBtn')} ${dt(['w-full'])}`}>
                                            <Button
                                                className={`${cx('btn', 'btnViewOrders')} ${dt([
                                                    'w-full',
                                                    'mx-auto',
                                                ])}`}
                                                buttonType="Secondary"
                                                size="Lg"
                                                onClick={this.gotoMyOrders}
                                            >
                                                View Order History
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h2
                                            className={dt([
                                                'mb-4',
                                                'text-xlarge',
                                                'leading-8',
                                                'font-semibold',
                                                'text-center',
                                                'text-black',
                                            ])}
                                        >
                                            Returning Customer?
                                        </h2>
                                        <p
                                            className={`${cx('subText')} ${dt([
                                                'mb-6',
                                                'float-left',
                                                'w-full',
                                                'text-medium',
                                                'leading-[1.5]',
                                                'text-gray-70',
                                                'text-left',
                                            ])}`}
                                        >
                                            {messagesTexts.trackOrder?.signinText}
                                        </p>
                                        <div className={`${cx('viewOrderBtn')} ${dt(['w-full'])}`}>
                                            <Button
                                                className={`${cx('btn', 'btnSignIn')} ${dt([
                                                    'lg:w-9/12',
                                                    'smMd:w-full',
                                                    'mx-auto',
                                                ])}`}
                                                size="Lg"
                                                buttonType="Secondary"
                                                onClick={this.gotoSignIn}
                                            >
                                                Sign In
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default TrackOrders;
